import { useEffect, useMemo, useState } from 'react'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import Typography from '@mui/material/Typography'
import { ErrorState } from '@/shared/components/ErrorState'
import { ListPageCountBadge } from '@/shared/components/ListPageCountBadge'
import { SensemakerPageTitle } from '@/shared/components/SensemakerPageTitle'
import { StandardPageTitleRow } from '@/shared/components/StandardPageTitleRow'
import { useDocumentTitle } from '@/shared/hooks/useDocumentTitle'
import { useToast } from '@/shared/toast'
import type { ISensemakerListCard } from '@brushfire/types'
import { useSensemakerList } from '../api/sensemaker-list-queries'
import {
  useDeactivateSensemaker,
  useReactivateSensemaker,
} from '../api/sensemaker-list-mutations'
import { SensemakerCardGrid } from '../components/SensemakerCardGrid'
import { SensemakerListEmptyState } from '../components/SensemakerListEmptyState'
import { SensemakerListFilters } from '../components/SensemakerListFilters'
import { SensemakerListPageSkeleton } from '../components/SensemakerListPageSkeleton'
import { SensemakerQuickStatPills } from '../components/SensemakerQuickStatPills'
import {
  SensemakerDeactivateModal,
  SensemakerReactivateModal,
} from '../components/SensemakerStatusModals'
import {
  DEFAULT_SENSEMAKER_LIST_FILTERS,
  filterSensemakerCards,
  type SensemakerListFilterState,
} from '../lib/sensemaker-list-filters'
import {
  canLoadMoreSensemakers,
  getVisibleSensemakerCount,
  nextSensemakerVisibleLimit,
  sliceVisibleSensemakers,
  SENSEMAKER_INITIAL_VISIBLE,
} from '../lib/sensemaker-list-pagination'
import {
  SENSEMAKER_LIST_PILLS_TO_GRID_GAP_PX,
} from '../lib/sensemaker-list-layout'
import { useSensemakerListSearch } from '../lib/sensemaker-list-search'

export function TenantListPage() {
  useDocumentTitle('Sensemaker List')
  const { showToast } = useToast()
  const { search: sensemakerSearch, setSearch: setSensemakerSearch } = useSensemakerListSearch()
  const { data, isLoading, isError, refetch } = useSensemakerList()
  const deactivateMutation = useDeactivateSensemaker()
  const reactivateMutation = useReactivateSensemaker()

  const [filters, setFilters] = useState<Omit<SensemakerListFilterState, 'search'>>({
    status: DEFAULT_SENSEMAKER_LIST_FILTERS.status,
    package: DEFAULT_SENSEMAKER_LIST_FILTERS.package,
  })
  const [visibleLimit, setVisibleLimit] = useState(SENSEMAKER_INITIAL_VISIBLE)
  const [pendingCard, setPendingCard] = useState<ISensemakerListCard | null>(null)
  const [modalMode, setModalMode] = useState<'deactivate' | 'reactivate' | null>(null)

  const activeFilters = useMemo(
    (): SensemakerListFilterState => ({ ...filters, search: sensemakerSearch }),
    [filters, sensemakerSearch],
  )

  useEffect(() => {
    setVisibleLimit(SENSEMAKER_INITIAL_VISIBLE)
  }, [sensemakerSearch])

  const filteredItems = useMemo(
    () => filterSensemakerCards(data?.items ?? [], activeFilters),
    [data?.items, activeFilters],
  )

  const visibleItems = useMemo(
    () => sliceVisibleSensemakers(filteredItems, visibleLimit),
    [filteredItems, visibleLimit],
  )

  const summary = data?.summary ?? {
    total: 0,
    active: 0,
    deactivated: 0,
    expired: 0,
    nearTokenLimit: 0,
  }

  const handleFiltersChange = (next: SensemakerListFilterState) => {
    setFilters({ status: next.status, package: next.package })
    setVisibleLimit(SENSEMAKER_INITIAL_VISIBLE)
  }

  const clearAllFilters = () => {
    setFilters({
      status: DEFAULT_SENSEMAKER_LIST_FILTERS.status,
      package: DEFAULT_SENSEMAKER_LIST_FILTERS.package,
    })
    setSensemakerSearch('')
    setVisibleLimit(SENSEMAKER_INITIAL_VISIBLE)
  }

  const closeModal = () => {
    if (deactivateMutation.isPending || reactivateMutation.isPending) return
    setModalMode(null)
    setPendingCard(null)
  }

  const confirmStatusChange = async () => {
    if (!pendingCard || !modalMode) return
    try {
      if (modalMode === 'deactivate') {
        await deactivateMutation.mutateAsync(pendingCard.tenantId)
        showToast(`${pendingCard.tenantName} deactivated`, 'success')
      } else {
        await reactivateMutation.mutateAsync(pendingCard.tenantId)
        showToast(`${pendingCard.tenantName} reactivated`, 'success')
      }
      closeModal()
    } catch {
      showToast('Unable to update tenant status. Please try again.', 'error')
    }
  }

  if (isLoading) {
    return <SensemakerListPageSkeleton />
  }

  if (isError || !data) {
    return <ErrorState onRetry={() => void refetch()} />
  }

  const showing = getVisibleSensemakerCount(filteredItems.length, visibleLimit)
  const showLoadMore = canLoadMoreSensemakers(filteredItems.length, visibleLimit)

  return (
    <Box data-testid="sensemaker-list-page">
      <StandardPageTitleRow
        actions={<SensemakerListFilters filters={activeFilters} onFiltersChange={handleFiltersChange} />}
      >
        <SensemakerPageTitle
          title="Sensemaker List"
          description="Manage and monitor all tenant organizations"
          badge={
            <ListPageCountBadge testId="sensemaker-list-total-badge">
              {summary.total} Sensemakers
            </ListPageCountBadge>
          }
        />
      </StandardPageTitleRow>

      <SensemakerQuickStatPills
        summary={summary}
        filters={activeFilters}
        onFiltersChange={handleFiltersChange}
      />

      {filteredItems.length === 0 ? (
        <SensemakerListEmptyState onClearFilters={clearAllFilters} />
      ) : (
        <>
          <SensemakerCardGrid
            cards={visibleItems}
            onDeactivate={(card) => {
              setPendingCard(card)
              setModalMode('deactivate')
            }}
            onReactivate={(card) => {
              setPendingCard(card)
              setModalMode('reactivate')
            }}
          />
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'flex-end',
              gap: 2,
              mt: `${SENSEMAKER_LIST_PILLS_TO_GRID_GAP_PX}px`,
            }}
          >
            <Typography
              sx={{ color: '#4a5565', fontSize: '14px', letterSpacing: '-0.1504px' }}
              data-testid="sensemaker-list-showing-count"
            >
              Showing {showing} of {filteredItems.length}
            </Typography>
            {showLoadMore ? (
              <Button
                variant="outlined"
                data-testid="sensemaker-list-load-more"
                onClick={() => setVisibleLimit((current) => nextSensemakerVisibleLimit(current))}
                sx={{
                  textTransform: 'none',
                  borderRadius: '8px',
                  borderColor: '#e1e7f3',
                  color: '#04183f',
                  fontSize: '14px',
                  fontWeight: 500,
                  px: 2,
                  py: '10px',
                  '&:hover': { borderColor: '#008cd0', bgcolor: '#ffffff' },
                }}
              >
                Load Next 24
              </Button>
            ) : null}
          </Box>
        </>
      )}

      <SensemakerDeactivateModal
        open={modalMode === 'deactivate' && pendingCard !== null}
        tenantName={pendingCard?.tenantName ?? ''}
        pending={deactivateMutation.isPending}
        onCancel={closeModal}
        onConfirm={() => void confirmStatusChange()}
      />
      <SensemakerReactivateModal
        open={modalMode === 'reactivate' && pendingCard !== null}
        tenantName={pendingCard?.tenantName ?? ''}
        pending={reactivateMutation.isPending}
        onCancel={closeModal}
        onConfirm={() => void confirmStatusChange()}
      />
    </Box>
  )
}
